import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plane, Building2, Utensils, MapPin, Calendar, Users, 
  DollarSign, Sparkles, Send, Mic, MicOff, Loader2, X,
  ChevronRight, ChevronDown, Clock, Star, Heart, Crown,
  Mountain, Briefcase, User, Landmark, Wallet, Bot,
  Sun, Sunset, Moon, Coffee, Camera, Map, Compass,
  Check, ArrowRight, Globe, Ticket, ShoppingCart, Plus,
  Minus, Trash2, CreditCard, ExternalLink, Save, Eye,
  Package, Shield, Wifi, Car, ChevronUp, CloudSun
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { FloatingBackButton } from '../components/layout/FloatingBackButton';
import { AssistantChat } from '../components/assistant/TravelAssistant';
import { WeatherCard, WeatherRecommendations } from '../components/weather/WeatherIntelligence';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Speech Recognition API availability
const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
const SPEECH_RECOGNITION_AVAILABLE = Boolean(SpeechRecognitionAPI);

// Travel style icons
const STYLE_ICONS = {
  luxury: Crown,
  budget: Wallet,
  family: Users,
  honeymoon: Heart,
  solo: User,
  adventure: Mountain,
  business: Briefcase,
  cultural: Landmark
};

// Popular destinations
const POPULAR_DESTINATIONS = [
  { name: 'Paris', tagline: 'City of Light', image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600&q=80' },
  { name: 'Bali', tagline: 'Island of Gods', image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=600&q=80' },
  { name: 'Dubai', tagline: 'City of Dreams', image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=600&q=80' },
  { name: 'Tokyo', tagline: 'Where Past Meets Future', image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=600&q=80' },
  { name: 'Maldives', tagline: 'Paradise on Earth', image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=600&q=80' },
  { name: 'Switzerland', tagline: "Nature's Masterpiece", image: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=600&q=80' },
];

// Section Header Component
const SectionHeader = ({ eyebrow, title, subtitle, accentColor = '#00E5FF' }) => (
  <div className="mb-8">
    <p className="text-sm uppercase tracking-[0.3em] mb-2 font-medium" style={{ color: accentColor }}>
      {eyebrow}
    </p>
    <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white font-bold mb-2">
      {title}
    </h2>
    {subtitle && <p className="text-white/50 max-w-2xl">{subtitle}</p>}
  </div>
);

// Day Card Component
const DayCard = ({ day, isExpanded, onToggle }) => {
  const timeIcons = { morning: Sun, afternoon: Sunset, evening: Moon };
  
  return (
    <motion.div
      layout
      className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden"
    >
      <button
        onClick={onToggle}
        className="w-full p-5 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF007F] to-[#7B2FBE] flex items-center justify-center">
            <span className="text-white font-bold text-lg">{day.day}</span>
          </div>
          <div className="text-left">
            <h3 className="text-white font-semibold text-lg">Day {day.day}</h3>
            <p className="text-white/60 text-sm">{day.title}</p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-white/50 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-white/10"
          >
            <div className="p-5 space-y-4">
              {['morning', 'afternoon', 'evening'].map((time) => {
                const TimeIcon = timeIcons[time];
                const activity = day[time];
                if (!activity) return null;
                
                return (
                  <div key={time} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        time === 'morning' ? 'bg-amber-500/20 text-amber-400' :
                        time === 'afternoon' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-indigo-500/20 text-indigo-400'
                      }`}>
                        <TimeIcon className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-white font-medium capitalize">{time}</h4>
                        {activity.duration && (
                          <span className="text-white/40 text-xs flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {activity.duration}
                          </span>
                        )}
                      </div>
                      <p className="text-[#00E5FF] font-medium mb-1">{activity.activity}</p>
                      <p className="text-white/60 text-sm">{activity.description}</p>
                      {activity.tip && (
                        <p className="text-[#FF007F] text-xs mt-2 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Pro tip: {activity.tip}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Meals */}
              {day.meals && (
                <div className="pt-4 border-t border-white/10">
                  <h4 className="text-white/50 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Utensils className="w-3 h-3" /> Where to Eat
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    {['breakfast', 'lunch', 'dinner'].map((meal) => day.meals[meal] && (
                      <div key={meal} className="bg-white/5 rounded-lg p-2 text-center">
                        <p className="text-white/40 text-xs capitalize">{meal}</p>
                        <p className="text-white text-sm truncate">{day.meals[meal]}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Bookable Hotel Card
const BookableHotelCard = ({ hotel, type, onAddToCart, inCart }) => {
  const colors = {
    luxury: '#FFD700',
    mid_range: '#00E5FF',
    budget: '#4ADE80'
  };
  
  return (
    <div className={`bg-white/5 rounded-xl p-4 border transition-all ${
      inCart ? 'border-[#4ADE80]/50 bg-[#4ADE80]/5' : 'border-white/10 hover:border-[#FF007F]/30'
    }`}>
      <div className="flex items-start justify-between mb-2">
        <h4 className="text-white font-medium">{hotel.name}</h4>
        <span className="text-xs px-2 py-0.5 rounded-full capitalize" style={{ backgroundColor: `${colors[type]}20`, color: colors[type] }}>
          {type.replace('_', ' ')}
        </span>
      </div>
      <p className="text-white/60 text-sm mb-2">{hotel.area}</p>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[#00E5FF] font-semibold">{hotel.price_per_night}<span className="text-white/40 text-xs">/night</span></p>
      </div>
      {hotel.why && <p className="text-white/50 text-xs mb-3">{hotel.why}</p>}
      
      <button
        onClick={() => onAddToCart('hotel', {
          name: hotel.name,
          hotel_name: hotel.name,
          area: hotel.area,
          price: parseInt(hotel.price_per_night?.replace(/[^0-9]/g, '') || 0),
          type: type,
          why: hotel.why,
          booking_url: `https://www.booking.com/search?ss=${encodeURIComponent(hotel.name)}`
        })}
        disabled={inCart}
        className={`w-full py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
          inCart 
            ? 'bg-[#4ADE80]/20 text-[#4ADE80] cursor-default'
            : 'bg-[#FF007F]/20 text-[#FF007F] hover:bg-[#FF007F]/30'
        }`}
      >
        {inCart ? <><Check className="w-4 h-4" /> In Cart</> : <><Plus className="w-4 h-4" /> Add to Trip</>}
      </button>
    </div>
  );
};

// Bookable Experience Card
const BookableExperienceCard = ({ experience, onAddToCart, inCart }) => (
  <div className={`bg-white/5 rounded-xl p-4 border transition-all ${
    inCart ? 'border-[#4ADE80]/50 bg-[#4ADE80]/5' : 'border-white/10 hover:border-[#FF007F]/30'
  }`}>
    <div className="flex items-start justify-between mb-2">
      <h4 className="text-white font-medium">{experience.name}</h4>
      {experience.must_do && (
        <span className="text-xs px-2 py-0.5 rounded-full bg-[#FF007F]/20 text-[#FF007F]">
          Must Do
        </span>
      )}
    </div>
    <p className="text-white/60 text-sm mb-2">{experience.description}</p>
    <div className="flex items-center justify-between mb-3 text-sm">
      <span className="text-[#00E5FF]">{experience.estimated_cost}</span>
      <span className="text-white/40">{experience.duration}</span>
    </div>
    
    <button
      onClick={() => onAddToCart('experience', {
        name: experience.name,
        description: experience.description,
        price: parseInt(experience.estimated_cost?.replace(/[^0-9]/g, '') || 0),
        duration: experience.duration,
        category: experience.category,
        must_do: experience.must_do,
        booking_url: experience.booking_url || `https://www.viator.com/search/${encodeURIComponent(experience.name)}`
      })}
      disabled={inCart}
      className={`w-full py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
        inCart 
          ? 'bg-[#4ADE80]/20 text-[#4ADE80] cursor-default'
          : 'bg-[#FFD700]/20 text-[#FFD700] hover:bg-[#FFD700]/30'
      }`}
    >
      {inCart ? <><Check className="w-4 h-4" /> In Cart</> : <><Plus className="w-4 h-4" /> Add to Trip</>}
    </button>
  </div>
);

// Real Viator Experience Card (with actual API data)
const ViatorExperienceCard = ({ tour, onAddToCart, inCart }) => {
  const primaryImage = tour.images?.[0]?.url || 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&q=80';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white/5 rounded-xl overflow-hidden border transition-all ${
        inCart ? 'border-[#4ADE80]/50 bg-[#4ADE80]/5' : 'border-white/10 hover:border-[#FF007F]/30'
      }`}
    >
      <div className="relative h-32 overflow-hidden">
        <img 
          src={primaryImage}
          alt={tour.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        
        {/* Badges */}
        <div className="absolute top-2 left-2 flex gap-1">
          {tour.free_cancellation && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-500/90 text-white">
              Free Cancel
            </span>
          )}
        </div>
        
        {/* Rating */}
        {tour.rating && (
          <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/60 px-2 py-0.5 rounded-lg">
            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
            <span className="text-white text-xs font-medium">{tour.rating.toFixed(1)}</span>
          </div>
        )}
      </div>
      
      <div className="p-3">
        <h4 className="text-white font-medium text-sm line-clamp-2 mb-1">{tour.title}</h4>
        
        <div className="flex items-center justify-between mb-2 text-xs">
          <span className="text-[#00E5FF] font-semibold">{tour.price?.formatted}</span>
          {tour.duration_text && (
            <span className="text-white/40">{tour.duration_text}</span>
          )}
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => onAddToCart('experience', {
              name: tour.title,
              description: tour.description,
              price: tour.price?.amount || 0,
              duration: tour.duration_text,
              viator_code: tour.code,
              booking_url: tour.booking_url,
              image: primaryImage,
              rating: tour.rating
            })}
            disabled={inCart}
            className={`flex-1 py-1.5 rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition-all ${
              inCart 
                ? 'bg-[#4ADE80]/20 text-[#4ADE80] cursor-default'
                : 'bg-[#FFD700]/20 text-[#FFD700] hover:bg-[#FFD700]/30'
            }`}
          >
            {inCart ? <><Check className="w-3 h-3" /> Added</> : <><Plus className="w-3 h-3" /> Add</>}
          </button>
          <a
            href={tour.booking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 text-white/70 hover:bg-white/10 flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" /> Viator
          </a>
        </div>
      </div>
    </motion.div>
  );
};

// Viator Experiences Section - Fetches real tours from Viator API
const ViatorExperiencesSection = ({ destination, onAddToCart, isInCart }) => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (destination) {
      fetchViatorTours();
    }
  }, [destination]);
  
  const fetchViatorTours = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/tours/freetext`, {
        params: {
          q: destination,
          currency: 'INR',
          page_size: 6
        }
      });
      
      if (response.data.success && response.data.products) {
        setTours(response.data.products);
      }
    } catch (err) {
      console.error('Failed to fetch Viator tours:', err);
      setError('Unable to load experiences');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-[#FFD700] animate-spin" />
        <span className="text-white/50 ml-2">Finding top experiences...</span>
      </div>
    );
  }
  
  if (error || tours.length === 0) {
    return null;
  }
  
  return (
    <section className="px-4 md:px-16 py-16 bg-gradient-to-b from-[#09080E] to-[#0D0B14]">
      <div className="max-w-6xl mx-auto">
        <SectionHeader 
          eyebrow="Powered by Viator" 
          title="Book Real Experiences" 
          subtitle="Top-rated tours and activities you can book instantly"
          accentColor="#FFD700"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tours.map((tour) => (
            <ViatorExperienceCard 
              key={tour.code} 
              tour={tour}
              onAddToCart={onAddToCart}
              inCart={isInCart('experience', tour.title)}
            />
          ))}
        </div>
        <div className="mt-6 text-center">
          <a
            href={`https://aira-connect.preview.emergentagent.com/tours?destination=${encodeURIComponent(destination)}`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#FF007F]/20 to-[#7B2FBE]/20 text-[#FF007F] font-medium hover:from-[#FF007F]/30 hover:to-[#7B2FBE]/30 transition-all border border-[#FF007F]/30"
          >
            <Globe className="w-5 h-5" />
            Explore All {destination} Experiences
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Trip Cart Sidebar Component
const TripCartSidebar = ({ cart, isOpen, onClose, onRemoveItem, onToggleItem, onCheckout, onSave }) => {
  const items = cart?.items || {};
  const pricing = cart?.pricing || {};
  
  const totalItems = Object.values(items).reduce((sum, arr) => sum + (arr?.length || 0), 0);
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-[#0D0B14] border-l border-white/10 z-50 flex flex-col overflow-hidden"
            data-testid="trip-cart-sidebar"
          >
            {/* Header */}
            <div className="p-6 border-b border-white/10 bg-gradient-to-b from-[#1a1625] to-transparent">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-white/70" />
              </button>
              
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF007F] to-[#00E5FF] p-0.5">
                  <div className="w-full h-full rounded-2xl bg-[#12101A] flex items-center justify-center">
                    <ShoppingCart className="w-7 h-7 text-[#00E5FF]" />
                  </div>
                </div>
                <div>
                  <h2 className="font-['Playfair_Display'] text-xl text-white font-bold">Your Trip Cart</h2>
                  <p className="text-white/50 text-sm">{totalItems} {totalItems === 1 ? 'item' : 'items'} selected</p>
                </div>
              </div>
            </div>
            
            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {totalItems === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">Your trip cart is empty</p>
                  <p className="text-white/30 text-sm mt-2">Add flights, hotels, and experiences from the itinerary</p>
                </div>
              ) : (
                <>
                  {/* Flights */}
                  {items.flights?.length > 0 && (
                    <div>
                      <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                        <Plane className="w-4 h-4" /> Flights
                      </h3>
                      {items.flights.map((item) => (
                        <CartItemCard key={item.item_id} item={item} type="flight" onRemove={onRemoveItem} onToggle={onToggleItem} />
                      ))}
                    </div>
                  )}
                  
                  {/* Hotels */}
                  {items.hotels?.length > 0 && (
                    <div>
                      <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                        <Building2 className="w-4 h-4" /> Hotels
                      </h3>
                      {items.hotels.map((item) => (
                        <CartItemCard key={item.item_id} item={item} type="hotel" onRemove={onRemoveItem} onToggle={onToggleItem} />
                      ))}
                    </div>
                  )}
                  
                  {/* Experiences */}
                  {items.experiences?.length > 0 && (
                    <div>
                      <h3 className="text-white/50 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                        <Ticket className="w-4 h-4" /> Experiences
                      </h3>
                      {items.experiences.map((item) => (
                        <CartItemCard key={item.item_id} item={item} type="experience" onRemove={onRemoveItem} onToggle={onToggleItem} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Pricing Summary */}
            {totalItems > 0 && (
              <div className="p-4 border-t border-white/10 bg-gradient-to-t from-[#0D0B14] to-transparent space-y-3">
                <div className="bg-white/5 rounded-xl p-4 space-y-2">
                  {pricing.flights_total > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">Flights</span>
                      <span className="text-white">₹{pricing.flights_total?.toLocaleString()}</span>
                    </div>
                  )}
                  {pricing.hotels_total > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">Hotels</span>
                      <span className="text-white">₹{pricing.hotels_total?.toLocaleString()}</span>
                    </div>
                  )}
                  {pricing.experiences_total > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">Experiences</span>
                      <span className="text-white">₹{pricing.experiences_total?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="border-t border-white/10 pt-2 flex justify-between text-sm">
                    <span className="text-white/60">Service Fee (2.5%)</span>
                    <span className="text-white">₹{pricing.service_fee?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Taxes (5% GST)</span>
                    <span className="text-white">₹{pricing.taxes?.toLocaleString()}</span>
                  </div>
                  <div className="border-t border-white/10 pt-2 flex justify-between">
                    <span className="text-white font-semibold">Grand Total</span>
                    <span className="text-[#00E5FF] font-bold text-lg">₹{pricing.grand_total?.toLocaleString()}</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={onSave}
                    className="py-3 rounded-xl bg-white/5 border border-white/10 text-white font-medium flex items-center justify-center gap-2 hover:bg-white/10 transition-all"
                  >
                    <Save className="w-4 h-4" /> Save Trip
                  </button>
                  <button
                    onClick={onCheckout}
                    className="py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white font-semibold flex items-center justify-center gap-2 hover:shadow-[0_0_30px_rgba(255,0,127,0.4)] transition-all"
                  >
                    <CreditCard className="w-4 h-4" /> Checkout
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Cart Item Card
const CartItemCard = ({ item, type, onRemove, onToggle }) => {
  const icons = { flight: Plane, hotel: Building2, experience: Ticket };
  const Icon = icons[type] || Package;
  
  return (
    <div className={`bg-white/5 rounded-xl p-3 mb-2 border transition-all ${
      item.selected ? 'border-white/10' : 'border-white/5 opacity-50'
    }`}>
      <div className="flex items-start gap-3">
        <button
          onClick={() => onToggle(type, item.item_id, !item.selected)}
          className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition-all ${
            item.selected 
              ? 'bg-[#4ADE80] border-[#4ADE80]' 
              : 'border-white/30 hover:border-white/50'
          }`}
        >
          {item.selected && <Check className="w-3 h-3 text-black" />}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <Icon className="w-4 h-4 text-[#00E5FF]" />
              <p className="text-white font-medium text-sm truncate">{item.name}</p>
            </div>
            <button
              onClick={() => onRemove(type, item.item_id)}
              className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-red-400 transition-all"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          
          {item.provider && (
            <p className="text-white/40 text-xs mt-1">{item.provider}</p>
          )}
          
          <div className="flex items-center justify-between mt-2">
            <p className="text-[#00E5FF] font-semibold">₹{item.price?.toLocaleString()}</p>
            {item.booking_url && (
              <a
                href={item.booking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-white/40 hover:text-[#FF007F] flex items-center gap-1"
              >
                View <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Trip Planner Page
export default function TripPlannerPage() {
  const { token } = useAuth();
  const { isDark, colors } = useTheme();
  const navigate = useNavigate();
  
  // State
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [itinerary, setItinerary] = useState(null);
  const [expandedDays, setExpandedDays] = useState([1]);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [travelStyles, setTravelStyles] = useState([]);
  const [heroImage, setHeroImage] = useState('https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920&q=80');
  
  // Cart State
  const [cart, setCart] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCreatingCart, setIsCreatingCart] = useState(false);
  
  // View Mode
  const [viewMode, setViewMode] = useState('explore'); // 'explore' | 'book'
  
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch travel styles
  useEffect(() => {
    fetchTravelStyles();
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    if (SPEECH_RECOGNITION_AVAILABLE && SpeechRecognitionAPI) {
      const recognition = new SpeechRecognitionAPI();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInputText(transcript);
        
        if (event.results[0].isFinal) {
          setIsListening(false);
        }
      };

      recognition.onerror = () => setIsListening(false);
      recognition.onend = () => setIsListening(false);
      recognitionRef.current = recognition;
    }

    return () => recognitionRef.current?.abort();
  }, []);

  const fetchTravelStyles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/trip-planner/styles`);
      if (response.data.success) {
        setTravelStyles(response.data.styles);
      }
    } catch (error) {
      console.error('Failed to fetch travel styles:', error);
    }
  };

  const toggleListening = () => {
    const hasSpeech = Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!hasSpeech) {
      toast.error('Voice recognition not supported. Use Chrome or Edge.');
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      setInputText('');
      recognitionRef.current?.start();
      setIsListening(true);
    }
  };

  const generateItinerary = async () => {
    if (!inputText.trim()) {
      toast.error('Please describe your trip');
      return;
    }

    setIsGenerating(true);
    setItinerary(null);
    setCart(null);

    try {
      const response = await axios.post(`${API_URL}/api/trip-planner/generate`, {
        request: inputText,
        preferences: selectedStyle ? { style: selectedStyle } : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setItinerary(response.data.itinerary);
        setExpandedDays([1]);
        
        // Update hero image if we have cover data
        if (response.data.itinerary.cover?.hero) {
          setHeroImage(response.data.itinerary.cover.hero);
        }
        
        toast.success('Your personalized itinerary is ready!');
      } else {
        toast.error(response.data.message || 'Failed to generate itinerary');
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate itinerary. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const createTripCart = async () => {
    if (!itinerary) return;
    
    setIsCreatingCart(true);
    try {
      const response = await axios.post(`${API_URL}/api/trip-planner/cart/create`, {
        itinerary: itinerary,
        trip_name: `${itinerary.duration} Days in ${itinerary.destination}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setCart(response.data.cart);
        setViewMode('book');
        toast.success('Trip cart created! Start adding items.');
      }
    } catch (error) {
      console.error('Cart creation error:', error);
      toast.error('Failed to create trip cart');
    } finally {
      setIsCreatingCart(false);
    }
  };

  const addToCart = async (itemType, itemData) => {
    if (!cart) {
      toast.error('Please create a trip cart first');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/trip-planner/cart/${cart.cart_id}/add-item`, {
        item_type: itemType,
        item_data: itemData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setCart(response.data.cart);
        toast.success(`${itemData.name} added to cart`);
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      toast.error('Failed to add item');
    }
  };

  const removeFromCart = async (itemType, itemId) => {
    if (!cart) return;
    
    try {
      const response = await axios.delete(
        `${API_URL}/api/trip-planner/cart/${cart.cart_id}/item/${itemType}/${itemId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setCart(response.data.cart);
        toast.success('Item removed');
      }
    } catch (error) {
      console.error('Remove from cart error:', error);
      toast.error('Failed to remove item');
    }
  };

  const toggleCartItem = async (itemType, itemId, selected) => {
    if (!cart) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/trip-planner/cart/${cart.cart_id}/toggle-item`, {
        item_type: itemType,
        item_id: itemId,
        selected: selected
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setCart(response.data.cart);
      }
    } catch (error) {
      console.error('Toggle item error:', error);
    }
  };

  const saveTrip = async () => {
    if (!cart) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/trip-planner/cart/${cart.cart_id}/save`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Trip saved! You can access it anytime from your profile.');
      }
    } catch (error) {
      console.error('Save trip error:', error);
      toast.error('Failed to save trip');
    }
  };

  const prepareCheckout = async () => {
    if (!cart) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/trip-planner/cart/${cart.cart_id}/checkout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const checkout = response.data.checkout;
        
        // Show checkout summary with booking links
        toast.success('Checkout ready! Opening booking pages...');
        
        // Open booking URLs for each selected item type
        const bookingUrls = checkout.booking_urls || {};
        Object.values(bookingUrls).flat().forEach((item, idx) => {
          if (item?.url) {
            setTimeout(() => {
              window.open(item.url, '_blank');
            }, idx * 500);
          }
        });
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed');
    }
  };

  const isInCart = (itemType, itemName) => {
    if (!cart?.items) return false;
    const items = cart.items[`${itemType}s`] || [];
    return items.some(item => item.name === itemName);
  };

  const handleDestinationClick = (destination) => {
    setInputText(`Plan a 5-day trip to ${destination}`);
    inputRef.current?.focus();
  };

  const toggleDayExpansion = (day) => {
    setExpandedDays(prev => 
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const cartItemCount = cart ? Object.values(cart.items || {}).reduce((sum, arr) => sum + (arr?.length || 0), 0) : 0;

  // Theme-aware colors
  const bgPrimary = isDark ? '#09080E' : '#FAFAFA';
  const bgSecondary = isDark ? '#0D0B14' : '#F5F5F7';
  const textPrimary = isDark ? '#ffffff' : '#1a1a2e';
  const textSecondary = isDark ? 'rgba(255,255,255,0.6)' : '#4a4a5e';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)';
  const borderColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
  const accentPrimary = isDark ? '#FF007F' : '#E6006E';
  const accentSecondary = isDark ? '#00E5FF' : '#0891B2';
  const accentGold = isDark ? '#FFD700' : '#D97706';

  return (
    <div 
      className="min-h-screen transition-colors duration-300" 
      style={{ backgroundColor: bgPrimary }}
      data-testid="trip-planner-page"
    >
      <FloatingBackButton />
      <section className="mx-auto max-w-4xl px-4 pt-20 pb-6" data-testid="ai-trip-planner-section">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-2" style={{ color: textPrimary }}>AI Trip Planner</h1>
        <p className="text-sm mb-4" style={{ color: textSecondary }}>Powered by our ChatGPT assistant with real flight, hotel and Viator tour searches. Prices from search results are live quotes; everything else is marked as an estimate.</p>
        <AssistantChat mode="planner" className="h-[620px] rounded-3xl border border-white/10 overflow-hidden" />
      </section>

      {/* Cart Button */}
      {cart && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          onClick={() => setIsCartOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-16 h-16 rounded-full bg-gradient-to-r from-[#FF007F] to-[#00E5FF] shadow-[0_0_40px_rgba(255,0,127,0.5)] flex items-center justify-center"
          data-testid="cart-btn"
        >
          <ShoppingCart className="w-7 h-7 text-white" />
          {cartItemCount > 0 && (
            <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#FFD700] text-black text-xs font-bold flex items-center justify-center">
              {cartItemCount}
            </span>
          )}
        </motion.button>
      )}

      {/* Cart Sidebar */}
      <TripCartSidebar
        cart={cart}
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        onRemoveItem={removeFromCart}
        onToggleItem={toggleCartItem}
        onCheckout={prepareCheckout}
        onSave={saveTrip}
      />

      {/* ==================== HERO SECTION ==================== */}
      <section className="relative min-h-[80vh] overflow-hidden">
        {/* Dynamic Background */}
        <motion.div 
          key={heroImage}
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          className="absolute inset-0"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09080E]/80 via-[#09080E]/40 to-[#09080E]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#09080E]/60 via-transparent to-[#09080E]/60" />
        </motion.div>

        {/* Hero Content */}
        <div className="relative z-10 flex flex-col justify-center min-h-[80vh] px-4 md:px-16 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="max-w-4xl"
          >
            <p className="text-[#FF007F] text-sm uppercase tracking-[0.3em] mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              AI Trip Planner
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl lg:text-7xl text-white font-bold leading-tight mb-6">
              Your Dream Trip, <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF007F] via-[#00E5FF] to-[#FF007F]">
                Planned by AI
              </span>
            </h1>
            <p className="text-white/60 text-lg md:text-xl mb-10 max-w-2xl">
              Describe your perfect getaway and watch as AIRA creates a complete, personalized itinerary with flights, hotels, experiences, and local recommendations.
            </p>

            {/* Main Input */}
            <div className="bg-black/40 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-2xl">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Voice/Text Input */}
                <div className="flex-1 relative">
                  <textarea
                    ref={inputRef}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    placeholder='Try: "Plan a 5-day Bali trip under ₹80,000" or "Create a romantic Paris honeymoon itinerary"'
                    disabled={isGenerating}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder-white/30 focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F] transition-all resize-none h-24 disabled:opacity-50"
                    data-testid="trip-input"
                  />
                  
                  {/* Voice Button */}
                  <button
                    onClick={toggleListening}
                    disabled={isGenerating}
                    className={`absolute right-3 top-3 p-2 rounded-xl transition-all ${
                      isListening
                        ? 'bg-[#FF007F] text-white shadow-[0_0_20px_rgba(255,0,127,0.5)]'
                        : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid="voice-btn"
                  >
                    {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </button>
                </div>

                {/* Generate Button */}
                <button
                  onClick={generateItinerary}
                  disabled={isGenerating || !inputText.trim()}
                  className="md:w-auto w-full bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white font-semibold py-4 px-8 rounded-2xl hover:shadow-[0_0_30px_rgba(255,0,127,0.4)] transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                  data-testid="generate-btn"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Creating Your Trip...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Plan My Trip
                    </>
                  )}
                </button>
              </div>

              {/* Voice Listening Indicator */}
              <AnimatePresence>
                {isListening && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 p-3 rounded-xl bg-[#FF007F]/10 border border-[#FF007F]/30"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-[#FF007F] animate-pulse" />
                      <span className="text-[#FF007F] text-sm">Listening... Describe your dream trip</span>
                    </div>
                    <div className="flex items-center justify-center gap-1 mt-2">
                      {[16, 28, 12, 24, 8, 20, 14, 26, 10, 22, 18, 24].map((h, i) => (
                        <motion.div
                          key={i}
                          animate={{ height: [4, h, 4] }}
                          transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.05 }}
                          className="w-1 bg-[#FF007F] rounded-full"
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Travel Style Selector */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-white/40 text-sm mb-3">Travel Style</p>
                <div className="flex flex-wrap gap-2">
                  {travelStyles.map((style) => {
                    const StyleIcon = STYLE_ICONS[style.id] || Globe;
                    return (
                      <button
                        key={style.id}
                        onClick={() => setSelectedStyle(selectedStyle === style.id ? null : style.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                          selectedStyle === style.id
                            ? 'bg-[#FF007F]/20 text-[#FF007F] border border-[#FF007F]/50'
                            : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <StyleIcon className="w-4 h-4" />
                        {style.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== GENERATED ITINERARY ==================== */}
      <AnimatePresence>
        {itinerary && itinerary.type === 'itinerary' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Itinerary Hero */}
            <section className="relative py-20 overflow-hidden">
              <div className="absolute inset-0">
                <div 
                  className="absolute inset-0 bg-cover bg-center opacity-20"
                  style={{ backgroundImage: `url(${itinerary.cover?.hero || heroImage})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#09080E] via-transparent to-[#09080E]" />
              </div>
              
              <div className="relative z-10 px-4 md:px-16">
                <div className="max-w-4xl mx-auto text-center">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <p className="text-[#FF007F] text-sm uppercase tracking-[0.3em] mb-4">
                      Your Personalized Itinerary
                    </p>
                    <h2 className="font-['Playfair_Display'] text-4xl md:text-6xl text-white font-bold mb-4">
                      {itinerary.duration} Days in {itinerary.destination}
                    </h2>
                    <p className="text-white/60 text-lg mb-6 max-w-2xl mx-auto">
                      {itinerary.overview}
                    </p>
                    
                    {/* Highlights */}
                    {itinerary.highlights && (
                      <div className="flex flex-wrap justify-center gap-2 mb-8">
                        {itinerary.highlights.slice(0, 5).map((highlight, idx) => (
                          <span key={idx} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/70 text-sm">
                            {highlight}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Weather Intelligence Section */}
                    <div className="mb-8 max-w-2xl mx-auto">
                      <div className="flex items-center justify-center gap-2 mb-4">
                        <CloudSun className="w-5 h-5 text-[var(--color-gold)]" />
                        <span className="text-white/50 text-sm uppercase tracking-wider">Weather Intelligence</span>
                      </div>
                      <WeatherCard destination={itinerary.destination} compact />
                    </div>

                    {/* Budget Overview */}
                    {itinerary.budget && (
                      <div className="inline-flex items-center gap-6 px-6 py-3 rounded-2xl bg-white/5 border border-white/10 mb-8">
                        <div className="text-center">
                          <p className="text-white/50 text-xs uppercase">Total Budget</p>
                          <p className="text-[#00E5FF] text-2xl font-bold">₹{itinerary.budget.total?.toLocaleString()}</p>
                        </div>
                        <div className="w-px h-10 bg-white/10" />
                        <div className="text-center">
                          <p className="text-white/50 text-xs uppercase">Style</p>
                          <p className="text-white font-medium capitalize">{itinerary.travel_style}</p>
                        </div>
                        <div className="w-px h-10 bg-white/10" />
                        <div className="text-center">
                          <p className="text-white/50 text-xs uppercase">Best Time</p>
                          <p className="text-white font-medium">{itinerary.best_time_to_visit}</p>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons - Explore vs Book */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <button
                        onClick={() => setViewMode('explore')}
                        className={`px-8 py-4 rounded-2xl font-semibold flex items-center justify-center gap-3 transition-all ${
                          viewMode === 'explore'
                            ? 'bg-white/10 border-2 border-[#00E5FF] text-[#00E5FF]'
                            : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10'
                        }`}
                      >
                        <Eye className="w-5 h-5" />
                        Explore Itinerary
                      </button>
                      
                      <button
                        onClick={cart ? () => { setViewMode('book'); setIsCartOpen(true); } : createTripCart}
                        disabled={isCreatingCart}
                        className={`px-8 py-4 rounded-2xl font-semibold flex items-center justify-center gap-3 transition-all ${
                          viewMode === 'book' || cart
                            ? 'bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white shadow-[0_0_30px_rgba(255,0,127,0.3)]'
                            : 'bg-[#FF007F]/20 border border-[#FF007F]/50 text-[#FF007F] hover:bg-[#FF007F]/30'
                        }`}
                        data-testid="book-trip-btn"
                      >
                        {isCreatingCart ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <ShoppingCart className="w-5 h-5" />
                        )}
                        {cart ? `View Cart (${cartItemCount})` : 'Book Entire Trip'}
                      </button>
                    </div>
                  </motion.div>
                </div>
              </div>
            </section>

            {/* Quick Actions */}
            <section className="px-4 md:px-16 py-8 bg-[#0D0B14]">
              <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <button 
                    onClick={() => navigate('/travel')}
                    className="flex items-center justify-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-[#FF007F]/20 to-[#FF007F]/10 border border-[#FF007F]/30 hover:border-[#FF007F]/50 transition-all group"
                  >
                    <Plane className="w-5 h-5 text-[#FF007F]" />
                    <span className="text-white font-medium">Browse Flights</span>
                    <ArrowRight className="w-4 h-4 text-[#FF007F] group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button 
                    onClick={() => navigate('/stays')}
                    className="flex items-center justify-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-[#00E5FF]/20 to-[#00E5FF]/10 border border-[#00E5FF]/30 hover:border-[#00E5FF]/50 transition-all group"
                  >
                    <Building2 className="w-5 h-5 text-[#00E5FF]" />
                    <span className="text-white font-medium">Browse Hotels</span>
                    <ArrowRight className="w-4 h-4 text-[#00E5FF] group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button 
                    onClick={() => navigate('/experiences')}
                    className="flex items-center justify-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-[#FFD700]/20 to-[#FFD700]/10 border border-[#FFD700]/30 hover:border-[#FFD700]/50 transition-all group"
                  >
                    <Ticket className="w-5 h-5 text-[#FFD700]" />
                    <span className="text-white font-medium">Browse Experiences</span>
                    <ArrowRight className="w-4 h-4 text-[#FFD700] group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button 
                    onClick={saveTrip}
                    disabled={!cart}
                    className="flex items-center justify-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-[#4ADE80]/20 to-[#4ADE80]/10 border border-[#4ADE80]/30 hover:border-[#4ADE80]/50 transition-all group disabled:opacity-50"
                  >
                    <Save className="w-5 h-5 text-[#4ADE80]" />
                    <span className="text-white font-medium">Save Itinerary</span>
                    <Check className="w-4 h-4 text-[#4ADE80]" />
                  </button>
                </div>
              </div>
            </section>

            {/* Daily Itinerary */}
            {itinerary.daily_itinerary && (
              <section className="px-4 md:px-16 py-16 bg-[#09080E]">
                <div className="max-w-4xl mx-auto">
                  <SectionHeader 
                    eyebrow="Day by Day" 
                    title="Your Daily Itinerary" 
                    subtitle="A carefully crafted day-by-day guide to make the most of your trip"
                    accentColor="#FF007F"
                  />
                  <div className="space-y-4">
                    {itinerary.daily_itinerary.map((day) => (
                      <DayCard 
                        key={day.day} 
                        day={day} 
                        isExpanded={expandedDays.includes(day.day)}
                        onToggle={() => toggleDayExpansion(day.day)}
                      />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Accommodation */}
            {itinerary.accommodation && (
              <section className="px-4 md:px-16 py-16 bg-[#0D0B14]">
                <div className="max-w-6xl mx-auto">
                  <SectionHeader 
                    eyebrow="Where to Stay" 
                    title="Recommended Hotels" 
                    subtitle={cart ? 'Click "Add to Trip" to include in your booking' : `Our top pick: ${itinerary.accommodation.recommended}`}
                    accentColor="#00E5FF"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {itinerary.accommodation.luxury?.map((hotel, idx) => (
                      <BookableHotelCard 
                        key={`luxury-${idx}`} 
                        hotel={hotel} 
                        type="luxury" 
                        onAddToCart={addToCart}
                        inCart={isInCart('hotel', hotel.name)}
                      />
                    ))}
                    {itinerary.accommodation.mid_range?.map((hotel, idx) => (
                      <BookableHotelCard 
                        key={`mid-${idx}`} 
                        hotel={hotel} 
                        type="mid_range"
                        onAddToCart={addToCart}
                        inCart={isInCart('hotel', hotel.name)}
                      />
                    ))}
                    {itinerary.accommodation.budget?.map((hotel, idx) => (
                      <BookableHotelCard 
                        key={`budget-${idx}`} 
                        hotel={hotel} 
                        type="budget"
                        onAddToCart={addToCart}
                        inCart={isInCart('hotel', hotel.name)}
                      />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Experiences */}
            {itinerary.experiences && itinerary.experiences.length > 0 && (
              <section className="px-4 md:px-16 py-16 bg-[#09080E]">
                <div className="max-w-6xl mx-auto">
                  <SectionHeader 
                    eyebrow="Must-Do" 
                    title="Recommended Experiences" 
                    subtitle={cart ? 'Add activities to your trip cart' : undefined}
                    accentColor="#FFD700"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {itinerary.experiences.map((exp, idx) => (
                      <BookableExperienceCard 
                        key={idx} 
                        experience={exp}
                        onAddToCart={addToCart}
                        inCart={isInCart('experience', exp.name)}
                      />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Real Viator Experiences */}
            {itinerary.destination && (
              <ViatorExperiencesSection 
                destination={itinerary.destination}
                onAddToCart={addToCart}
                isInCart={isInCart}
              />
            )}

            {/* Food Guide */}
            {itinerary.food_guide && (
              <section className="px-4 md:px-16 py-16 bg-[#0D0B14]">
                <div className="max-w-6xl mx-auto">
                  <SectionHeader 
                    eyebrow="Culinary Journey" 
                    title="Food & Dining Guide" 
                    accentColor="#FF007F"
                  />
                  
                  {/* Must Try Dishes */}
                  {itinerary.food_guide.must_try_dishes && (
                    <div className="mb-8">
                      <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                        <Utensils className="w-5 h-5 text-[#FF007F]" /> Must-Try Local Dishes
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {itinerary.food_guide.must_try_dishes.map((dish, idx) => (
                          <span key={idx} className="px-4 py-2 rounded-full bg-[#FF007F]/10 border border-[#FF007F]/30 text-white">
                            {dish}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Fine Dining */}
                    {itinerary.food_guide.fine_dining && (
                      <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                        <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                          <Star className="w-4 h-4 text-[#FFD700]" /> Fine Dining
                        </h4>
                        <div className="space-y-3">
                          {itinerary.food_guide.fine_dining.map((r, idx) => (
                            <div key={idx} className="flex justify-between items-start">
                              <div>
                                <p className="text-white font-medium">{r.name}</p>
                                <p className="text-white/50 text-sm">{r.specialty}</p>
                              </div>
                              <span className="text-[#FFD700] text-sm">{r.price_range}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Local Favorites */}
                    {itinerary.food_guide.local_favorites && (
                      <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                        <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                          <Heart className="w-4 h-4 text-[#FF007F]" /> Local Favorites
                        </h4>
                        <div className="space-y-3">
                          {itinerary.food_guide.local_favorites.map((r, idx) => (
                            <div key={idx} className="flex justify-between items-start">
                              <div>
                                <p className="text-white font-medium">{r.name}</p>
                                <p className="text-white/50 text-sm">{r.specialty}</p>
                              </div>
                              <span className="text-[#00E5FF] text-sm">{r.price_range}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Street Food & Cafes */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    {itinerary.food_guide.street_food && (
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <h4 className="text-white/70 text-sm uppercase tracking-wider mb-3">Street Food</h4>
                        <ul className="space-y-2">
                          {itinerary.food_guide.street_food.map((item, idx) => (
                            <li key={idx} className="text-white/80 text-sm flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#FF007F]" /> {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {itinerary.food_guide.cafes && (
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <h4 className="text-white/70 text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Coffee className="w-4 h-4" /> Cafes
                        </h4>
                        <ul className="space-y-2">
                          {itinerary.food_guide.cafes.map((item, idx) => (
                            <li key={idx} className="text-white/80 text-sm flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF]" /> {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* Practical Tips */}
            {itinerary.practical_tips && (
              <section className="px-4 md:px-16 py-16 bg-[#09080E]">
                <div className="max-w-4xl mx-auto">
                  <SectionHeader 
                    eyebrow="Travel Smart" 
                    title="Practical Tips" 
                    accentColor="#00E5FF"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {itinerary.practical_tips.map((tip, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10">
                        <div className="w-8 h-8 rounded-lg bg-[#00E5FF]/20 flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4 text-[#00E5FF]" />
                        </div>
                        <p className="text-white/80 text-sm">{tip}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Booking CTA */}
            {cart && cartItemCount > 0 && (
              <section className="px-4 md:px-16 py-16 bg-gradient-to-b from-[#0D0B14] to-[#09080E]">
                <div className="max-w-2xl mx-auto text-center">
                  <div className="bg-gradient-to-r from-[#FF007F]/20 via-[#7B2FBE]/20 to-[#00E5FF]/20 rounded-3xl p-8 border border-white/10">
                    <ShoppingCart className="w-12 h-12 text-[#FF007F] mx-auto mb-4" />
                    <h3 className="font-['Playfair_Display'] text-2xl text-white font-bold mb-2">
                      Ready to Book Your Trip?
                    </h3>
                    <p className="text-white/60 mb-6">
                      You have {cartItemCount} items in your cart worth ₹{cart.pricing?.grand_total?.toLocaleString()}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <button
                        onClick={() => setIsCartOpen(true)}
                        className="px-8 py-4 rounded-2xl bg-white/10 border border-white/20 text-white font-medium hover:bg-white/20 transition-all"
                      >
                        Review Cart
                      </button>
                      <button
                        onClick={prepareCheckout}
                        className="px-8 py-4 rounded-2xl bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white font-semibold hover:shadow-[0_0_30px_rgba(255,0,127,0.4)] transition-all flex items-center justify-center gap-2"
                      >
                        <CreditCard className="w-5 h-5" />
                        Proceed to Checkout
                      </button>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== POPULAR DESTINATIONS (when no itinerary) ==================== */}
      {!itinerary && !isGenerating && (
        <section className="px-4 md:px-16 py-16 bg-[#09080E]">
          <div className="max-w-6xl mx-auto">
            <SectionHeader 
              eyebrow="Get Inspired" 
              title="Popular Destinations" 
              subtitle="Click on any destination to start planning your trip"
              accentColor="#FF007F"
            />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {POPULAR_DESTINATIONS.map((dest) => (
                <motion.button
                  key={dest.name}
                  onClick={() => handleDestinationClick(dest.name)}
                  whileHover={{ y: -5 }}
                  className="relative overflow-hidden rounded-2xl aspect-[4/3] group"
                >
                  <img 
                    src={dest.image} 
                    alt={dest.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-['Playfair_Display'] text-white text-2xl font-bold">{dest.name}</h3>
                    <p className="text-white/60 text-sm">{dest.tagline}</p>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ==================== LOADING STATE ==================== */}
      <AnimatePresence>
        {isGenerating && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="px-4 md:px-16 py-32 bg-[#09080E]"
          >
            <div className="max-w-md mx-auto text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                className="w-20 h-20 mx-auto mb-8"
              >
                <div className="w-full h-full rounded-full border-4 border-[#FF007F]/20 border-t-[#FF007F]" />
              </motion.div>
              <h3 className="font-['Playfair_Display'] text-2xl text-white font-bold mb-4">
                Creating Your Perfect Trip
              </h3>
              <p className="text-white/60 mb-8">
                AIRA is researching flights, hotels, experiences, and local gems to craft your personalized itinerary...
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {['Analyzing destinations', 'Finding best flights', 'Curating hotels', 'Discovering experiences', 'Planning daily activities'].map((step, idx) => (
                  <motion.span
                    key={step}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.5 }}
                    className="px-3 py-1 rounded-full bg-white/5 text-white/50 text-sm"
                  >
                    {step}
                  </motion.span>
                ))}
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* ==================== INSPIRATION QUOTE ==================== */}
      {!itinerary && !isGenerating && (
        <section className="px-4 md:px-16 py-32 bg-[#0D0B14] text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Compass className="w-12 h-12 text-[#FF007F] mx-auto mb-8" />
            <blockquote className="font-['Playfair_Display'] text-3xl md:text-5xl text-white font-bold max-w-4xl mx-auto leading-tight mb-8">
              &ldquo;Travel is the only thing you buy that makes you richer.&rdquo;
            </blockquote>
            <p className="text-white/50 uppercase tracking-widest">— Anonymous</p>
          </motion.div>
        </section>
      )}
    </div>
  );
}
